/**
 * Houseplant v2 — PDP gallery
 *
 * Three jobs:
 *   1. Thumbnail click / keyboard scrolls the matching slide into the stage.
 *   2. Variant change filters slides + thumbs by the alt-text convention
 *      (see snippets/hp-product-gallery.liquid).
 *   3. Pausing video that the stage has moved away from.
 *
 * Playback itself is NOT here. Video slides are base's <deferred-media>, which
 * swaps the real player into the frame on click — see the snippet's header.
 *
 * Filtering is done locally by toggling the `hidden` attribute Liquid already
 * set — no Section Rendering round-trip, so switching colour is instant. The
 * base theme's media-gallery.js/product-info.js media reconciliation is
 * deliberately NOT used here; this gallery owns its own DOM.
 */
/**
 * Mirror of Liquid's `handleize`, so alt text classified server-side and option
 * values read client-side agree on what "Sand" normalises to.
 */
function handleize(value) {
  return String(value)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

class HpProductGallery extends HTMLElement {
  connectedCallback() {
    this.slidesList = this.querySelector('.hp-gallery__slides');
    this.thumbs = Array.from(this.querySelectorAll('.hp-gallery__thumb'));

    this.thumbs.forEach((thumb) => {
      thumb.querySelector('button')?.addEventListener('click', () => {
        this.showMedia(thumb.dataset.mediaId);
      });
    });

    // Keep the active thumbnail in sync when the user swipes the stage on mobile.
    this.slidesList?.addEventListener('scroll', this.onStageScroll.bind(this), { passive: true });

    /**
     * We listen to the raw `change` on the variant picker rather than the
     * theme's `variant:change` event, because the latter only fires after
     * product-info.js completes its Section Rendering fetch. Filtering images
     * shouldn't wait on the network.
     */
    this.onPickerChange = this.onPickerChange.bind(this);
    document.addEventListener('change', this.onPickerChange);

    this.setActiveThumb(this.visibleSlides()[0]?.dataset.mediaId);
    this.scheduleWarm();
  }

  /**
   * Browsers do NOT fetch `loading="lazy"` images inside a `display:none`
   * container — which is every non-selected colour. Without this, clicking
   * Sand starts every Sand image from zero and the stage flashes empty.
   *
   * So once the page is idle we warm the FIRST slide of each other colour
   * (the one the switch lands on) plus the hidden thumbnails, which are small.
   * Deliberately not every hidden slide: the rest lazy-load as the stage is
   * scrolled, and prefetching them all would undo the point of lazy loading.
   */
  scheduleWarm() {
    // Don't spend someone else's data plan on images they may never see.
    const conn = navigator.connection;
    if (conn?.saveData) return;
    if (/(^|-)2g$/.test(conn?.effectiveType || '')) return;

    const run = () => this.warmHiddenMedia();
    const idle = () =>
      'requestIdleCallback' in window
        ? window.requestIdleCallback(run, { timeout: 3000 })
        : window.setTimeout(run, 700);

    // Wait for load so warming never competes with the LCP image.
    if (document.readyState === 'complete') idle();
    else window.addEventListener('load', idle, { once: true });
  }

  warmHiddenMedia() {
    const warmed = new Set([this.dataset.selected]);
    const targets = [];

    this.querySelectorAll('.hp-gallery__slide[data-hp-media-for]').forEach((slide) => {
      const value = slide.dataset.hpMediaFor;
      if (!value || warmed.has(value)) return;
      warmed.add(value);
      targets.push(slide.querySelector('img'));
    });

    this.querySelectorAll('.hp-gallery__thumb[hidden] img').forEach((img) => targets.push(img));

    targets.forEach((img) => {
      if (!img) return;
      const warm = new Image();
      // sizes BEFORE srcset: candidate selection reads sizes, and we want the
      // warm fetch to resolve to the same URL the visible <img> will request,
      // otherwise we've filled the cache with a file nothing asks for.
      if (img.sizes) warm.sizes = img.sizes;
      if (img.srcset) warm.srcset = img.srcset;
      warm.src = img.src;
    });
  }

  disconnectedCallback() {
    document.removeEventListener('change', this.onPickerChange);
  }

  onPickerChange(event) {
    const picker = event.target.closest?.('variant-selects');
    if (!picker) return;

    // Ignore pickers belonging to a different product on the page (quick-view, etc).
    if (picker.closest('product-info') !== this.closest('product-info')) return;

    const value = this.readOptionValue(picker);
    if (value) this.filterBy(handleize(value));
  }

  /** Pull the currently selected value for THIS gallery's driving option. */
  readOptionValue(picker) {
    const target = this.dataset.optionHandle;

    /**
     * The base theme builds these names with `{% capture %}`, which leaves a
     * trailing newline in the attribute — the real value is "Color-1\n", not
     * "Color-1". JS `$` anchors at true end-of-string (unlike Perl/Python, it
     * does NOT match before a trailing newline), so the suffix strip silently
     * no-ops unless we trim first. Trim BEFORE the regex, not after.
     */
    const radio = Array.from(picker.querySelectorAll('input[type="radio"]:checked')).find((input) =>
      // Radio names are "{Option Name}-{position}", e.g. "Color-1".
      handleize(input.name.trim().replace(/-\d+$/, '')) === target
    );
    if (radio) return radio.value;

    const select = Array.from(picker.querySelectorAll('select')).find((el) =>
      // Select names are "options[{Option Name}]".
      handleize(el.name.trim().replace(/^options\[|\]$/g, '')) === target
    );
    return select?.value;
  }

  visibleSlides() {
    return Array.from(this.querySelectorAll('.hp-gallery__slide:not([hidden])'));
  }

  /**
   * Stop anything playing outside the slide now in the stage.
   *
   * Deliberately NOT window.pauseAllMedia(): that is page-wide, so scrolling
   * this gallery would also stop a video banner elsewhere on the page. Same
   * three selectors, scoped to this element.
   *
   * The postMessage calls only reach a player that was embedded with
   * `enablejsapi` — the snippet passes it for exactly this reason. Without it
   * they are accepted and ignored, and the symptom is a video that keeps
   * playing WITH SOUND from a slide nobody can see.
   */
  pauseMediaExcept(mediaId) {
    const keep = mediaId == null ? null : String(mediaId);

    this.querySelectorAll('.hp-gallery__slide--video').forEach((slide) => {
      if (keep !== null && slide.dataset.mediaId === keep) return;

      slide.querySelectorAll('video').forEach((video) => video.pause());
      slide.querySelectorAll('iframe.js-youtube').forEach((frame) => {
        frame.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
      });
      slide.querySelectorAll('iframe.js-vimeo').forEach((frame) => {
        frame.contentWindow?.postMessage('{"method":"pause"}', '*');
      });
    });
  }

  /**
   * Show only media belonging to `optionValue` (handleized) plus shared media
   * (those with an empty data-hp-media-for). Called by the owning section when
   * the variant changes.
   */
  filterBy(optionValue) {
    if (!optionValue || optionValue === this.dataset.selected) return;
    this.dataset.selected = optionValue;

    this.querySelectorAll('[data-hp-media-for]').forEach((el) => {
      const belongsTo = el.dataset.hpMediaFor;
      el.hidden = belongsTo !== '' && belongsTo !== optionValue;
    });

    /**
     * Before anything else: a `hidden` slide keeps playing, audio included.
     * Unconditional rather than left to setActiveThumb below, because a colour
     * with no media of its own leaves no visible slide to hand it.
     */
    this.pauseMediaExcept(null);

    // Jump to the first image of the newly selected colour.
    const first = this.visibleSlides()[0];
    if (first) this.showMedia(first.dataset.mediaId, 'auto');
  }

  showMedia(mediaId, behavior = 'smooth') {
    const slide = this.querySelector(`.hp-gallery__slide[data-media-id="${mediaId}"]`);
    if (!slide || slide.hidden) return;

    this.slidesList?.scrollTo({ left: slide.offsetLeft - this.slidesList.offsetLeft, behavior });
    this.setActiveThumb(mediaId);
  }

  /**
   * The single funnel for "which slide is in the stage" — called by
   * showMedia (so by thumbnails and by the variant filter) and by the scroll
   * settle. Pausing here rather than in each caller is what makes a swipe past
   * a playing video stop it as reliably as a thumbnail click does.
   */
  setActiveThumb(mediaId) {
    this.pauseMediaExcept(mediaId);

    this.thumbs.forEach((thumb) => {
      const isActive = thumb.dataset.mediaId === String(mediaId);
      thumb.classList.toggle('is-active', isActive);
      thumb.querySelector('button')?.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }

  onStageScroll() {
    clearTimeout(this.scrollTimer);
    this.scrollTimer = setTimeout(() => {
      const { scrollLeft } = this.slidesList;
      const nearest = this.visibleSlides().reduce((best, slide) => {
        const offset = Math.abs(slide.offsetLeft - this.slidesList.offsetLeft - scrollLeft);
        return !best || offset < best.offset ? { slide, offset } : best;
      }, null);
      if (nearest) this.setActiveThumb(nearest.slide.dataset.mediaId);
    }, 80);
  }
}

if (!customElements.get('hp-product-gallery')) {
  customElements.define('hp-product-gallery', HpProductGallery);
}
